/**
 * EmailBuilder - Main drag-and-drop email builder component
 * 
 * A visual editor for creating HTML emails with rows, columns, and content blocks.
 */

import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { 
  DndContext, 
  DragEndEvent, 
  DragOverlay,
  DragOverEvent,
  DragStartEvent, 
  PointerSensor, 
  useSensor, 
  useSensors,
  pointerWithin,
  rectIntersection,
  MeasuringStrategy,
  CollisionDetection,
} from '@dnd-kit/core';
import { useEmailBuilder } from './builder/useEmailBuilder';
import { BuilderCanvas } from './builder/BuilderCanvas';
import { BuilderSidebar } from './builder/BuilderSidebar';
import { generateEmailHtml } from './builder/htmlEmailGenerator';
import { BlockType, EmailDocument } from './builder/types';
import { Eye, Code, Save, Undo2, Redo2, Type, Image, MousePointer, Minus, ArrowUpDown, Check, Loader2, Moon, Sun } from 'lucide-react';

interface EmailBuilderProps {
  initialDocument?: EmailDocument;
  onSave: (html: string, document: EmailDocument, subject?: string) => void;
  practiceName?: string;
  bookingLink?: string;
  initialSubject?: string;
  templateName?: string; // Used as default subject if subject is empty
}

// Auto-save debounce delay in milliseconds
const AUTO_SAVE_DELAY = 1500;

// Sample data for preview - replaces template variables with realistic values
const PREVIEW_SAMPLE_DATA: Record<string, string> = {
  '{{pet_owner_name}}': 'Sarah Johnson',
  '{{pet_owner_first_name}}': 'Sarah',
  '{{pet_owner_email}}': 'sarah.johnson@email.com',
  '{{pet_owner_phone}}': '(555) 123-4567',
  '{{owner_pet_name}}': 'Turbo',
  '{{has_have}}': 'has',
  '{{is/are}}': 'is',
  '{{is_are}}': 'is',
  '{{practice_name}}': '', // Will be replaced with actual practice name
  '{{practice_phone}}': '(555) 987-6543',
  '{{practice_email}}': 'info@example.vet',
  '{{practice_address}}': '123 Veterinary Lane, Suite 100, Pet City, CA 90210',
  '{{pet_name}}': 'Turbo',
  '{{pet_names}}': 'Turbo and Bella',
  '{{next_appointment_date}}': 'January 15, 2026 at 2:30 PM',
  '{{appointment_date}}': 'January 15, 2026 at 2:30 PM',  // Backwards compatibility
  '{{form_link}}': 'https://example.com/forms/intake',
  '{{book_appointment_link}}': 'https://helppet.ai/book/ca/pet-city/happy-paws-vet',
  // Overdue vaccine table variables
  '{{vaccine_name}}': 'Rabies',
  '{{scheduled_date}}': 'December 20, 2025',
  '{{days_overdue}}': '17',
  // Legacy variable aliases
  '{{name}}': 'Sarah Johnson',
  '{{first_name}}': 'Sarah',
  '{{email}}': 'sarah.johnson@email.com',
  '{{phone}}': '(555) 123-4567',
};

const VACCINE_LOOP_REGEX = /\{\{#(overdue_vaccines|scheduled_vaccines|all_vaccines)\}\}([\s\S]*?)\{\{\/\1\}\}/g;

// Sample vaccine data for realistic preview
const SAMPLE_VACCINES = [
  { pet_name: 'Turbo', vaccine: 'Rabies', scheduled_date: 'Dec 20, 2025', days_overdue: '27' },
  { pet_name: 'Turbo', vaccine: 'Distemper', scheduled_date: 'Dec 15, 2025', days_overdue: '32' },
  { pet_name: 'Bella', vaccine: 'Bordetella', scheduled_date: 'Jan 2, 2026', days_overdue: '14' },
];

function expandVaccinePreviewLoops(html: string): string {
  return html.replace(VACCINE_LOOP_REGEX, (_match, listType: string, rowTemplate: string) => {
    const status = listType === 'scheduled_vaccines' ? 'Scheduled' : 'Overdue';
    
    // Generate multiple sample rows for realistic preview
    return SAMPLE_VACCINES.map((vaccine, index) => {
      const bgColor = index % 2 === 0 ? '#ffffff' : '#f9fafb';
      return rowTemplate
        .replace(/\{\{#is_overdue\}\}/g, '')
        .replace(/\{\{\/is_overdue\}\}/g, '')
        .replace(/\{\{pet_name\}\}/gi, vaccine.pet_name)
        .replace(/\{\{vaccine_name\}\}/gi, vaccine.vaccine)
        .replace(/\{\{vaccine\}\}/gi, vaccine.vaccine)
        .replace(/\{\{scheduled_date\}\}/gi, vaccine.scheduled_date)
        .replace(/\{\{days_overdue\}\}/gi, vaccine.days_overdue)
        .replace(/\{\{status\}\}/g, status)
        .replace(/\{\{row_bg_color\}\}/g, bgColor);
    }).join('');
  });
}

// Replace template variables with sample data for preview
function applyPreviewData(html: string, practiceName: string, bookingLink?: string): string {
  let result = expandVaccinePreviewLoops(html);
  const bookingUrl = bookingLink || PREVIEW_SAMPLE_DATA['{{book_appointment_link}}'];
  
  // Replace double-brace variables {{variable}}
  result = result.replace(/\{\{pet_owner_name\}\}/g, PREVIEW_SAMPLE_DATA['{{pet_owner_name}}']);
  result = result.replace(/\{\{pet_owner_first_name\}\}/g, PREVIEW_SAMPLE_DATA['{{pet_owner_first_name}}']);
  result = result.replace(/\{\{pet_owner_email\}\}/g, PREVIEW_SAMPLE_DATA['{{pet_owner_email}}']);
  result = result.replace(/\{\{pet_owner_phone\}\}/g, PREVIEW_SAMPLE_DATA['{{pet_owner_phone}}']);
  result = result.replace(/\{\{owner_pet_name\}\}/g, PREVIEW_SAMPLE_DATA['{{owner_pet_name}}']);
  result = result.replace(/\{\{has_have\}\}/g, PREVIEW_SAMPLE_DATA['{{has_have}}']);
  result = result.replace(/\{\{is\/are\}\}/g, PREVIEW_SAMPLE_DATA['{{is/are}}']);
  result = result.replace(/\{\{is_are\}\}/g, PREVIEW_SAMPLE_DATA['{{is_are}}']);
  result = result.replace(/\{\{practice_name\}\}/g, practiceName);
  result = result.replace(/\{\{practice_phone\}\}/g, PREVIEW_SAMPLE_DATA['{{practice_phone}}']);
  result = result.replace(/\{\{practice_email\}\}/g, PREVIEW_SAMPLE_DATA['{{practice_email}}']);
  result = result.replace(/\{\{practice_address\}\}/g, PREVIEW_SAMPLE_DATA['{{practice_address}}']);
  result = result.replace(/\{\{pet_name\}\}/g, PREVIEW_SAMPLE_DATA['{{pet_name}}']);
  result = result.replace(/\{\{pet_names\}\}/g, PREVIEW_SAMPLE_DATA['{{pet_names}}']);
  result = result.replace(/\{\{next_appointment_date\}\}/g, PREVIEW_SAMPLE_DATA['{{next_appointment_date}}']);
  result = result.replace(/\{\{appointment_date\}\}/g, PREVIEW_SAMPLE_DATA['{{appointment_date}}']);
  result = result.replace(/\{\{form_link\}\}/g, PREVIEW_SAMPLE_DATA['{{form_link}}']);
  result = result.replace(/\{\{book_appointment_link\}\}/g, bookingUrl);
  // Overdue vaccine table variables
  result = result.replace(/\{\{vaccine_name\}\}/g, PREVIEW_SAMPLE_DATA['{{vaccine_name}}']);
  result = result.replace(/\{\{scheduled_date\}\}/g, PREVIEW_SAMPLE_DATA['{{scheduled_date}}']);
  result = result.replace(/\{\{days_overdue\}\}/g, PREVIEW_SAMPLE_DATA['{{days_overdue}}']);
  // Legacy aliases
  result = result.replace(/\{\{name\}\}/g, PREVIEW_SAMPLE_DATA['{{name}}']);
  result = result.replace(/\{\{first_name\}\}/g, PREVIEW_SAMPLE_DATA['{{first_name}}']);
  result = result.replace(/\{\{email\}\}/g, PREVIEW_SAMPLE_DATA['{{email}}']);
  result = result.replace(/\{\{phone\}\}/g, PREVIEW_SAMPLE_DATA['{{phone}}']);
  
  // Also handle single-brace format {variable}
  result = result.replace(/\{pet_owner_name\}/g, PREVIEW_SAMPLE_DATA['{{pet_owner_name}}']);
  result = result.replace(/\{pet_owner_first_name\}/g, PREVIEW_SAMPLE_DATA['{{pet_owner_first_name}}']);
  result = result.replace(/\{pet_owner_email\}/g, PREVIEW_SAMPLE_DATA['{{pet_owner_email}}']);
  result = result.replace(/\{pet_owner_phone\}/g, PREVIEW_SAMPLE_DATA['{{pet_owner_phone}}']);
  result = result.replace(/\{owner_pet_name\}/g, PREVIEW_SAMPLE_DATA['{{owner_pet_name}}']);
  result = result.replace(/\{has_have\}/g, PREVIEW_SAMPLE_DATA['{{has_have}}']);
  result = result.replace(/\{is\/are\}/g, PREVIEW_SAMPLE_DATA['{{is/are}}']);
  result = result.replace(/\{is_are\}/g, PREVIEW_SAMPLE_DATA['{{is_are}}']);
  result = result.replace(/\{practice_name\}/g, practiceName);
  result = result.replace(/\{practice_phone\}/g, PREVIEW_SAMPLE_DATA['{{practice_phone}}']);
  result = result.replace(/\{practice_email\}/g, PREVIEW_SAMPLE_DATA['{{practice_email}}']);
  result = result.replace(/\{practice_address\}/g, PREVIEW_SAMPLE_DATA['{{practice_address}}']);
  result = result.replace(/\{pet_name\}/g, PREVIEW_SAMPLE_DATA['{{pet_name}}']);
  result = result.replace(/\{pet_names\}/g, PREVIEW_SAMPLE_DATA['{{pet_names}}']);
  result = result.replace(/\{next_appointment_date\}/g, PREVIEW_SAMPLE_DATA['{{next_appointment_date}}']);
  result = result.replace(/\{appointment_date\}/g, PREVIEW_SAMPLE_DATA['{{appointment_date}}']);
  result = result.replace(/\{form_link\}/g, PREVIEW_SAMPLE_DATA['{{form_link}}']);
  result = result.replace(/\{book_appointment_link\}/g, bookingUrl);
  result = result.replace(/\{vaccine_name\}/g, PREVIEW_SAMPLE_DATA['{{vaccine_name}}']);
  result = result.replace(/\{scheduled_date\}/g, PREVIEW_SAMPLE_DATA['{{scheduled_date}}']);
  result = result.replace(/\{days_overdue\}/g, PREVIEW_SAMPLE_DATA['{{days_overdue}}']);
  result = result.replace(/\{name\}/g, PREVIEW_SAMPLE_DATA['{{name}}']);
  result = result.replace(/\{first_name\}/g, PREVIEW_SAMPLE_DATA['{{first_name}}']);
  result = result.replace(/\{email\}/g, PREVIEW_SAMPLE_DATA['{{email}}']);
  result = result.replace(/\{phone\}/g, PREVIEW_SAMPLE_DATA['{{phone}}']);
  
  return result;
}

// Apply dark mode styles to simulate email client dark mode rendering
// Most email clients invert light backgrounds and adjust text colors
function applyDarkModeStyles(html: string): string {
  let result = html;
  
  // Common light background colors to invert
  const lightTooDark: Record<string, string> = {
    '#ffffff': '#1a1a1a',
    '#fff': '#1a1a1a',
    'white': '#1a1a1a',
    '#f8fafc': '#0f172a', // slate-50 -> slate-900
    '#f1f5f9': '#1e293b', // slate-100 -> slate-800
    '#e2e8f0': '#334155', // slate-200 -> slate-700
    '#f3f4f6': '#1f2937', // gray-100 -> gray-800
    '#e5e7eb': '#374151', // gray-200 -> gray-700
    '#f9fafb': '#111827', // gray-50 -> gray-900
  };
  
  // Dark text colors to lighten
  const darkToLight: Record<string, string> = {
    '#000000': '#ffffff',
    '#000': '#ffffff',
    'black': '#ffffff',
    '#1e293b': '#e2e8f0', // slate-800 -> slate-200
    '#334155': '#cbd5e1', // slate-700 -> slate-300
    '#1f2937': '#e5e7eb', // gray-800 -> gray-200
    '#374151': '#d1d5db', // gray-700 -> gray-300
    '#111827': '#f3f4f6', // gray-900 -> gray-100
    '#1e3a8a': '#93c5fd', // blue-900 -> blue-300
    '#1e40af': '#93c5fd', // blue-800 -> blue-300
    '#2563eb': '#60a5fa', // blue-600 -> blue-400
  };
  
  // Replace background colors in style attributes
  for (const [light, dark] of Object.entries(lightTooDark)) {
    // Match background-color in style attributes (case insensitive)
    const bgRegex = new RegExp(`background-color:\\s*${light.replace('#', '\\#')}`, 'gi');
    result = result.replace(bgRegex, `background-color: ${dark}`);
    
    // Also match bgcolor attribute
    const bgAttrRegex = new RegExp(`bgcolor=["']${light}["']`, 'gi');
    result = result.replace(bgAttrRegex, `bgcolor="${dark}"`);
  }
  
  // Replace text colors
  for (const [dark, light] of Object.entries(darkToLight)) {
    const colorRegex = new RegExp(`color:\\s*${dark.replace('#', '\\#')}`, 'gi');
    result = result.replace(colorRegex, `color: ${light}`);
  }
  
  // Also update the body background
  result = result.replace(
    /background-color:\s*#f8fafc/gi, 
    'background-color: #0f172a'
  );
  
  return result;
}

export const EmailBuilder: React.FC<EmailBuilderProps> = ({
  initialDocument,
  onSave,
  practiceName = 'Your Practice',
  bookingLink,
  initialSubject = '',
  templateName = '',
}) => {
  const builder = useEmailBuilder();
  const [viewMode, setViewMode] = useState<'edit' | 'preview' | 'code'>('edit');
  const [generatedHtml, setGeneratedHtml] = useState<string>('');
  const [activeDragType, setActiveDragType] = useState<BlockType | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [darkModePreview, setDarkModePreview] = useState(false);
  const [emailSubject, setEmailSubject] = useState(initialSubject);
  const [subjectFocused, setSubjectFocused] = useState(false);
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const subjectCursorRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });

  // Template variables for subject line (excludes form_link and vax_table)
  const templateVariables = useMemo(() => [
    { key: 'pet_owner_name', label: 'Owner Name' },
    { key: 'pet_owner_first_name', label: 'Owner First' },
    { key: 'pet_owner_email', label: 'Owner Email' },
    { key: 'pet_owner_phone', label: 'Owner Phone' },
    { key: 'owner_pet_name', label: 'Pet Name' },
    { key: 'has_have', label: 'Has/Have' },
    { key: 'is/are', label: 'Is/Are' },
    { key: 'practice_name', label: 'Practice Name' },
    { key: 'practice_phone', label: 'Practice Phone' },
    { key: 'practice_email', label: 'Practice Email' },
    { key: 'practice_address', label: 'Practice Addr' },
    { key: 'next_appointment_date', label: 'Next Appt Date' },
    { key: 'book_appointment_link', label: 'Book Appt Link' },
  ], []);

  // Insert variable at cursor position in subject
  const insertSubjectVariable = useCallback((variableKey: string) => {
    const input = subjectInputRef.current;
    if (!input) return;

    const { start, end } = subjectCursorRef.current;
    const variableText = `{{${variableKey}}}`;
    const newValue = emailSubject.slice(0, start) + variableText + emailSubject.slice(end);
    
    setEmailSubject(newValue);
    
    // Restore focus and position cursor after the inserted variable
    setTimeout(() => {
      input.focus();
      const newPosition = start + variableText.length;
      input.setSelectionRange(newPosition, newPosition);
      subjectCursorRef.current = { start: newPosition, end: newPosition };
    }, 0);
  }, [emailSubject]);
  
  // Track row dragging for drop indicator
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const [overRowId, setOverRowId] = useState<string | null>(null);

  // Track if initial load is complete to avoid auto-save on mount
  const isInitialMount = useRef(true);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const documentVersionRef = useRef(0);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Custom collision detection that uses pointerWithin for columns (better for horizontal layouts)
  // Falls back to rectIntersection for other droppables
  const customCollisionDetection: CollisionDetection = useCallback((args) => {
    // First try pointerWithin - this is more accurate for side-by-side columns
    const pointerCollisions = pointerWithin(args);
    
    // If we found a column with the pointer inside, use that
    const columnCollision = pointerCollisions.find(
      collision => collision.data?.droppableContainer?.data?.current?.type === 'column'
    );
    if (columnCollision) {
      return [columnCollision];
    }
    
    // If pointer is over any droppable, use those results
    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }
    
    // Fallback to rectIntersection for cases where pointer isn't directly over a droppable
    return rectIntersection(args);
  }, []);

  // Load initial document if provided
  useEffect(() => {
    if (initialDocument) {
      builder.loadDocument(initialDocument);
    }
    // Mark initial mount as complete after a short delay
    const timer = setTimeout(() => {
      isInitialMount.current = false;
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Generate HTML whenever document changes
  useEffect(() => {
    const html = generateEmailHtml(builder.document, { practiceName });
    setGeneratedHtml(html);
  }, [builder.document, practiceName]);

  // Auto-save with debouncing
  useEffect(() => {
    // Skip auto-save on initial mount
    if (isInitialMount.current) return;
    
    // Skip if document has no rows (empty/reset state)
    if (builder.document.rows.length === 0) return;

    // Increment version to track this change
    documentVersionRef.current += 1;
    const currentVersion = documentVersionRef.current;

    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set saving indicator immediately to show user we're tracking changes
    setSaveStatus('saving');

    // Debounced auto-save
    saveTimeoutRef.current = setTimeout(() => {
      // Only save if this is still the latest version
      if (currentVersion === documentVersionRef.current) {
        const html = generateEmailHtml(builder.document, { practiceName });
        // Use subject, or template name as default, or generic default
        const subjectToSave = emailSubject || templateName || `Email from ${practiceName}`;
        onSave(html, builder.document, subjectToSave);
        setSaveStatus('saved');
        
        // Reset to idle after showing "saved" for a moment
        setTimeout(() => {
          setSaveStatus('idle');
        }, 2000);
      }
    }, AUTO_SAVE_DELAY);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [builder.document, practiceName, onSave, emailSubject, templateName]);

  // Handle manual save
  const handleSave = useCallback(() => {
    // Clear any pending auto-save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    setSaveStatus('saving');
    const subjectToSave = emailSubject || templateName || `Email from ${practiceName}`;
    onSave(generatedHtml, builder.document, subjectToSave);
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus('idle'), 2000);
  }, [onSave, generatedHtml, builder.document, emailSubject, templateName, practiceName]);

  // Handle drag start - track if dragging from palette or row
  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const data = active.data.current;
    
    if (data?.type === 'palette-block') {
      setActiveDragType(data.blockType as BlockType);
    } else if (data?.type === 'row') {
      setActiveRowId(active.id as string);
    }
  }, []);

  // Handle drag over - track which row we're hovering over
  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    
    // Only track for row dragging
    const activeData = active.data.current;
    if (activeData?.type !== 'row') {
      setOverRowId(null);
      return;
    }
    
    if (!over) {
      setOverRowId(null);
      return;
    }
    
    const overData = over.data.current;
    
    // If over a row directly
    if (overData?.type === 'row') {
      setOverRowId(over.id as string);
      return;
    }
    
    // If over a column, find its parent row
    if (overData?.type === 'column') {
      const columnId = overData.columnId as string;
      // Find which row contains this column
      for (const row of builder.document.rows) {
        if (row.columns.some(col => col.id === columnId)) {
          setOverRowId(row.id);
          return;
        }
      }
    }
    
    // If over a block, find its parent row
    if (overData?.type === 'block') {
      const columnId = overData.columnId as string;
      for (const row of builder.document.rows) {
        if (row.columns.some(col => col.id === columnId)) {
          setOverRowId(row.id);
          return;
        }
      }
    }
    
    setOverRowId(null);
  }, [builder.document.rows]);

  // Handle drag end - add block when dropped from palette to column
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragType(null);
    setActiveRowId(null);
    setOverRowId(null);

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // Handle palette block dropped on a column
    if (activeData?.type === 'palette-block' && overData?.type === 'column') {
      const blockType = activeData.blockType as BlockType;
      const columnId = overData.columnId as string;
      builder.addBlock(columnId, blockType);
      return;
    }

    // Handle row reordering
    if (activeData?.type === 'row' && overData?.type === 'row') {
      const fromIndex = builder.document.rows.findIndex(r => r.id === active.id);
      const toIndex = builder.document.rows.findIndex(r => r.id === over.id);
      if (fromIndex !== -1 && toIndex !== -1 && fromIndex !== toIndex) {
        builder.moveRow(fromIndex, toIndex);
      }
      return;
    }

    // Handle block reordering / moving between columns
    if (activeData?.type === 'block') {
      const blockId = activeData.blockId as string;
      const sourceColumnId = activeData.columnId as string;
      
      // Determine target column and index
      let targetColumnId: string;
      let targetIndex: number;
      
      if (overData?.type === 'block') {
        // Dropped on another block - insert at that block's position
        targetColumnId = overData.columnId as string;
        const overBlockId = overData.blockId as string;
        
        // Find the target index
        for (const row of builder.document.rows) {
          for (const col of row.columns) {
            if (col.id === targetColumnId) {
              targetIndex = col.blocks.findIndex(b => b.id === overBlockId);
              if (targetIndex === -1) targetIndex = col.blocks.length;
              break;
            }
          }
        }
      } else if (overData?.type === 'column') {
        // Dropped on empty column area
        targetColumnId = overData.columnId as string;
        targetIndex = 0; // Add at the beginning
      } else {
        return; // Invalid drop target
      }
      
      // Only move if actually changing position
      if (sourceColumnId !== targetColumnId || blockId !== over.id) {
        builder.moveBlock(blockId, targetColumnId, targetIndex!);
      }
    }
  }, [builder]);

  // Get icon for drag overlay
  const getDragOverlayIcon = (type: BlockType) => {
    const iconClass = "w-4 h-4";
    switch (type) {
      case 'text': return <Type className={iconClass} />;
      case 'image': return <Image className={iconClass} />;
      case 'button': return <MousePointer className={iconClass} />;
      case 'divider': return <Minus className={iconClass} />;
      case 'spacer': return <ArrowUpDown className={iconClass} />;
      default: return null;
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape key
      if (e.key === 'Escape') {
        if (viewMode !== 'edit') {
          setViewMode('edit');
        } else {
          builder.clearSelection();
        }
        return;
      }
      
      // Undo: Ctrl+Z (or Cmd+Z on Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        builder.undo();
        return;
      }
      
      // Redo: Ctrl+Shift+Z or Ctrl+Y (or Cmd+Shift+Z / Cmd+Y on Mac)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        builder.redo();
        return;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [viewMode, builder]);

  return (
    // Force light mode for email builder - emails are always light background
    <div 
      className="h-full flex flex-col" 
      style={{ 
        backgroundColor: '#f3f4f6', 
        colorScheme: 'light',
      }}
    >
      {/* Header - Subject + Controls */}
      <div className="px-4 py-3 bg-white border-b border-gray-200">
        <div className="flex items-center gap-3">
          {/* Subject Input - always visible */}
          <label htmlFor="email-subject" className="text-sm font-medium text-gray-700 whitespace-nowrap">
            Subject:
          </label>
          <div className="flex-1 relative">
            <input
              ref={subjectInputRef}
              id="email-subject"
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              onFocus={() => setSubjectFocused(true)}
              onBlur={() => setSubjectFocused(false)}
              onSelect={(e) => {
                const target = e.target as HTMLInputElement;
                subjectCursorRef.current = {
                  start: target.selectionStart || 0,
                  end: target.selectionEnd || 0,
                };
              }}
              placeholder={templateName || `Email from ${practiceName}`}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            {/* Variable insertion dropdown - appears below on focus */}
            {subjectFocused && (
              <div 
                className="absolute left-0 top-full mt-1 z-50"
                onMouseDown={(e) => e.preventDefault()}
              >
                <div className="w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-64 overflow-y-auto">
                  <div className="px-3 py-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide border-b border-gray-100">
                    Insert Variable
                  </div>
                  {templateVariables.map((variable) => (
                    <button
                      key={variable.key}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        insertSubjectVariable(variable.key);
                      }}
                      className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
                      title={`{{${variable.key}}}`}
                    >
                      {variable.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* View Mode Toggle */}
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setViewMode('edit')}
              className={`px-3 py-1.5 text-sm ${
                viewMode === 'edit'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Edit
            </button>
            <button
              onClick={() => setViewMode('preview')}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm border-l border-gray-200 ${
                viewMode === 'preview'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Eye className="w-3 h-3" />
              Preview
            </button>
            <button
              onClick={() => setViewMode('code')}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm border-l border-gray-200 ${
                viewMode === 'code'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Code className="w-3 h-3" />
              HTML
            </button>
          </div>

          {/* Undo/Redo */}
          <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={builder.undo}
              disabled={!builder.canUndo}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-sm transition-colors ${
                builder.canUndo
                  ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  : 'text-gray-300 cursor-not-allowed'
              }`}
              title="Undo (Ctrl+Z)"
            >
              <Undo2 className="w-4 h-4" />
            </button>
            <button
              onClick={builder.redo}
              disabled={!builder.canRedo}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-sm border-l border-gray-200 transition-colors ${
                builder.canRedo
                  ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  : 'text-gray-300 cursor-not-allowed'
              }`}
              title="Redo (Ctrl+Shift+Z)"
            >
              <Redo2 className="w-4 h-4" />
            </button>
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            className="flex items-center gap-2 px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
            title="Save now (auto-saves after changes)"
          >
            <Save className="w-4 h-4" />
            Save
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {viewMode === 'edit' ? (
          <DndContext
            sensors={sensors}
            collisionDetection={customCollisionDetection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            measuring={{
              droppable: {
                strategy: MeasuringStrategy.Always,
              },
            }}
          >
            {/* Canvas */}
            <BuilderCanvas
              document={builder.document}
              selection={builder.selection}
              onSelect={builder.select}
              onClearSelection={builder.clearSelection}
              onMoveRow={builder.moveRow}
              onMoveBlock={builder.moveBlock}
              onAddRow={builder.addRow}
              onDeleteRow={builder.deleteRow}
              onDuplicateRow={builder.duplicateRow}
              onDeleteBlock={builder.deleteBlock}
              onUpdateBlock={builder.updateBlock}
              activeRowId={activeRowId}
              overRowId={overRowId}
            />

            {/* Sidebar */}
            <BuilderSidebar
              state={{ 
                document: builder.document, 
                selection: builder.selection,
                canUndo: builder.canUndo,
                canRedo: builder.canRedo,
              }}
              actions={builder}
            />

            {/* Drag Overlay */}
            <DragOverlay>
              {activeDragType ? (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg shadow-lg">
                  {getDragOverlayIcon(activeDragType)}
                  <span className="text-sm font-medium capitalize">{activeDragType}</span>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : viewMode === 'preview' ? (
          /* Preview Mode - with sample data substitution and dark mode toggle */
          <div className={`flex-1 overflow-y-auto p-6 ${darkModePreview ? 'bg-gray-900' : 'bg-gray-200'}`}>
            {/* Dark Mode Toggle - Top Right */}
            <div className="max-w-3xl mx-auto mb-4 flex justify-end">
              <button
                onClick={() => setDarkModePreview(!darkModePreview)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  darkModePreview
                    ? 'bg-gray-800 text-yellow-400 border border-gray-700 hover:bg-gray-700'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
                title={darkModePreview ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {darkModePreview ? (
                  <>
                    <Sun className="w-4 h-4" />
                    Light Mode
                  </>
                ) : (
                  <>
                    <Moon className="w-4 h-4" />
                    Dark Mode
                  </>
                )}
              </button>
            </div>
            
            <div className={`max-w-3xl mx-auto rounded-lg shadow-lg overflow-hidden ${
              darkModePreview ? 'bg-gray-800' : 'bg-white'
            }`}>
              {/* Dark mode simulation - wraps email in dark container and inverts certain colors */}
              <div 
                className="pointer-events-none"
                style={darkModePreview ? {
                  backgroundColor: '#1a1a1a',
                  // Apply a subtle filter to simulate how email clients handle dark mode
                  // Most email clients invert light backgrounds and adjust text colors
                } : undefined}
                dangerouslySetInnerHTML={{ 
                  __html: darkModePreview 
                    ? applyDarkModeStyles(applyPreviewData(generatedHtml, practiceName, bookingLink))
                    : applyPreviewData(generatedHtml, practiceName, bookingLink) 
                }}
              />
            </div>
            
            {darkModePreview && (
              <p className="max-w-3xl mx-auto mt-4 text-center text-xs text-gray-500">
                Dark mode preview simulates how email clients like Gmail and Apple Mail render emails in dark mode.
                Actual appearance may vary by email client.
              </p>
            )}
          </div>
        ) : (
          /* Code Mode */
          <div className="flex-1 overflow-y-auto p-4">
            <div className="bg-gray-900 rounded-lg p-4 h-full overflow-auto">
              <pre className="text-green-400 text-sm font-mono whitespace-pre-wrap">
                {generatedHtml}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Save Status Toast - Fixed position, top right */}
      {saveStatus !== 'idle' && (
        <div 
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-2 rounded-lg shadow-lg transition-all duration-300 ${
            saveStatus === 'saving' 
              ? 'bg-gray-800 text-white' 
              : 'bg-green-600 text-white'
          }`}
          style={{
            animation: saveStatus === 'saved' ? 'fadeInOut 2s ease-in-out' : 'fadeIn 0.2s ease-out',
          }}
        >
          {saveStatus === 'saving' ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm font-medium">Saving...</span>
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              <span className="text-sm font-medium">Saved</span>
            </>
          )}
        </div>
      )}

      {/* Toast animation styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(-10px); }
          15% { opacity: 1; transform: translateY(0); }
          85% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-10px); }
        }
      `}</style>
    </div>
  );
};

export default EmailBuilder;

